import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { toast } from "react-toastify";
import { Plus, Edit, Trash2, Save, X, Calculator, Search, Download, Upload } from "lucide-react";
import { API } from "./adminConfig";

const InlineDosingForm = ({ formData, setFormData, onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[ContentForm] Submit started with formData:', formData);
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm"
            placeholder="e.g., BPC-157"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Category *</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm"
            required
          >
            <option value="Peptide">Peptide</option>
            <option value="Nootropic">Nootropic</option>
            <option value="Supplement">Supplement</option>
            <option value="Hormone">Hormone</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Dosing Range *</label>
          <input
            type="text"
            value={formData.dosing_range}
            onChange={(e) => setFormData({...formData, dosing_range: e.target.value})}
            className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm"
            placeholder="e.g., 250-500 mcg"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Frequency *</label>
          <input
            type="text"
            value={formData.frequency}
            onChange={(e) => setFormData({...formData, frequency: e.target.value})}
            className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm"
            placeholder="e.g., Daily"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Cycle Length</label>
          <input
            type="text"
            value={formData.cycle_length}
            onChange={(e) => setFormData({...formData, cycle_length: e.target.value})}
            className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm"
            placeholder="e.g., 4-6 Weeks"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Time Off Between Cycles</label>
          <input
            type="text"
            value={formData.time_off_between_cycles}
            onChange={(e) => setFormData({...formData, time_off_between_cycles: e.target.value})}
            className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm"
            placeholder="e.g., 4-8 Weeks"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Schedule</label>
          <input
            type="text"
            value={formData.schedule}
            onChange={(e) => setFormData({...formData, schedule: e.target.value})}
            className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm"
            placeholder="e.g., 5 days on, 2 days off"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Fasted</label>
          <select
            value={formData.fasted}
            onChange={(e) => setFormData({...formData, fasted: e.target.value})}
            className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm"
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="Optional">Optional</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Time of Day</label>
          <select
            value={formData.time_of_day}
            onChange={(e) => setFormData({...formData, time_of_day: e.target.value})}
            className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm"
          >
            <option value="AM">AM (Morning)</option>
            <option value="PM">PM (Evening)</option>
            <option value="Both">Both AM & PM</option>
            <option value="Anytime">Anytime</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-burgundy mb-1">Reconstitution</label>
          <select
            value={formData.reconstitution}
            onChange={(e) => setFormData({...formData, reconstitution: e.target.value})}
            className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm"
          >
            <option value="BAC Water">BAC Water</option>
            <option value="Acetic Acid + BAC">Acetic Acid + BAC</option>
            <option value="N/A">N/A (Pre-mixed)</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-burgundy mb-1">Titration Strategy</label>
        <textarea
          value={formData.titration_strategy}
          onChange={(e) => setFormData({...formData, titration_strategy: e.target.value})}
          className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm resize-none"
          rows={2}
          placeholder="Describe how to start low and gradually increase..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-burgundy mb-1">Uses & Benefits *</label>
        <textarea
          value={formData.uses_benefits}
          onChange={(e) => setFormData({...formData, uses_benefits: e.target.value})}
          className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm resize-none"
          rows={2}
          placeholder="Describe the main uses and benefits..."
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-burgundy mb-1">Watchouts & Precautions</label>
        <textarea
          value={formData.watchouts_precautions}
          onChange={(e) => setFormData({...formData, watchouts_precautions: e.target.value})}
          className="w-full px-3 py-2 border rounded focus:border-burgundy focus:outline-none text-sm resize-none"
          rows={2}
          placeholder="Important safety information and precautions..."
        />
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-burgundy text-white px-4 py-2 rounded text-sm hover:bg-burgundy/80 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Dosing Database Management Tab
const DosingDatabaseTab = () => {
  const [dosings, setDosings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDosingId, setEditingDosingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchDosings();
  }, []);

  useEffect(() => {
    // Auto-import if no dosings exist
    if (dosings.length === 0 && !loading) {
      bulkImportIfEmpty();
    }
  }, [dosings, loading]);

  const fetchDosings = async () => {
    try {
      const response = await axios.get(`${API}/admin/dosing-database`);
      setDosings(response.data || []);
    } catch (error) {
      console.error('Error fetching dosing data:', error);
      toast.error('Error loading dosing database');
    } finally {
      setLoading(false);
    }
  };

  const bulkImportIfEmpty = async () => {
    try {
      // Check if database is empty and import initial data
      const response = await axios.post(`${API}/admin/dosing-database/bulk-import`);
      if (response.data.success && response.data.message.includes('imported')) {
        toast.success(response.data.message);
        fetchDosings(); // Refresh the list
      }
    } catch (error) {
      console.error('Error bulk importing dosing data:', error);
    }
  };

  const exportDatabase = async () => {
    try {
      toast.info('Exporting dosing database...');
      const response = await axios.get(`${API}/admin/dosing-database/export`);
      
      // Create a blob and download it
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dosing_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.count} entries successfully!`);
    } catch (error) {
      console.error('Error exporting dosing data:', error);
      toast.error('Error exporting database');
    }
  };

  const importDatabase = async (file) => {
    try {
      toast.info('Reading import file...');
      
      const fileText = await file.text();
      const importData = JSON.parse(fileText);
      
      // Confirm before replacing data
      const confirmed = window.confirm(
        `⚠️ WARNING: This will REPLACE all ${dosings.length} existing entries with ${importData.data?.length || importData.length} entries from the file.\n\nThis action cannot be undone.\n\nContinue?`
      );
      
      if (!confirmed) {
        toast.info('Import cancelled');
        return;
      }
      
      toast.info('Importing data...');
      
      // Call the import API endpoint
      const response = await axios.post(`${API}/admin/dosing-database/import-from-json`, importData);
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        fetchDosings(); // Refresh the list
      } else {
        toast.error('Import failed');
      }
    } catch (error) {
      console.error('Error importing dosing data:', error);
      toast.error(error.response?.data?.detail || 'Error importing database. Please check the file format.');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('Please upload a JSON file');
        return;
      }
      importDatabase(file);
    }
    // Reset file input
    event.target.value = '';
  };

  const deleteDosing = async (dosingId) => {
    if (!window.confirm('Are you sure you want to delete this dosing entry?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/dosing-database/${dosingId}`);
      toast.success('Dosing entry deleted successfully');
      fetchDosings();
    } catch (error) {
      console.error('Error deleting dosing entry:', error);
      toast.error('Error deleting dosing entry');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
        <span className="ml-2 text-burgundy">Loading dosing database...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-burgundy font-serif">Dosing Database</h2>
          <p className="text-burgundy/70">
            Manage dosing information for calculator tools ({dosings.length} entries)
            <br />
            <span className="text-sm">Note: You can add new entries, edit existing ones, or import initial data.</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={exportDatabase} 
            variant="outline"
            className="border-green-600 text-green-600 hover:bg-green-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Data
          </Button>
          <label htmlFor="dosing-import-file">
            <Button 
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={() => document.getElementById('dosing-import-file').click()}
              type="button"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import Data
            </Button>
          </label>
          <input
            id="dosing-import-file"
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <Button 
            onClick={bulkImportIfEmpty} 
            variant="outline"
            className="border-burgundy text-burgundy hover:bg-burgundy/10"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button 
            onClick={() => {
              setShowAddForm(!showAddForm);
              if (!showAddForm) {
                setEditFormData({
                  name: '',
                  category: 'Peptide',
                  dosing_range: '',
                  frequency: '',
                  cycle_length: '',
                  timing: '',
                  schedule: '',
                  fasted: 'No',
                  time_of_day: 'AM',
                  reconstitution: 'BAC Water',
                  titration_strategy: '',
                  time_off_between_cycles: '',
                  uses_benefits: '',
                  watchouts_precautions: ''
                });
              }
            }} 
            className="bg-burgundy hover:bg-burgundy/80 text-cream"
          >
            <Plus className="h-4 w-4 mr-2" />
            {showAddForm ? 'Cancel' : 'Add New Entry'}
          </Button>
        </div>
      </div>

      {/* Instructions Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="text-sm text-burgundy space-y-2">
            <p className="font-semibold">📋 Database Sync Instructions:</p>
            <ol className="list-decimal ml-5 space-y-1">
              <li><strong>Export from Preview:</strong> Click "Export Data" to download your corrected data as JSON</li>
              <li><strong>Import to Production:</strong> In production admin panel, click "Import Data" and select the downloaded file</li>
              <li><strong>Confirm:</strong> Review the warning and confirm to replace production data</li>
            </ol>
            <p className="text-xs text-burgundy/70 mt-3">
              💡 Tip: Always export your current data before importing as a backup
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Inline Add Form */}
      {showAddForm && (
        <Card className="bg-blue-50 border-2 border-blue-300 mb-6">
          <CardHeader>
            <CardTitle className="text-burgundy">Add New Dosing Entry</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-burgundy mb-4">
              ⚠️ <strong>Note:</strong> Due to the large form, please scroll down within this card to fill all fields.
            </div>
            <div className="max-h-[600px] overflow-y-auto pr-2">
              <InlineDosingForm
                formData={editFormData}
                setFormData={setEditFormData}
                onSave={async () => {
                  try {
                    await axios.post(`${API}/admin/dosing-database`, editFormData);
                    toast.success('Entry created!');
                    setShowAddForm(false);
                    fetchDosings();
                  } catch (error) {
                    console.error('Error creating entry:', error);
                    toast.error('Failed to create entry');
                  }
                }}
                onCancel={() => setShowAddForm(false)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dosing Entries Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {dosings.map((dosing) => (
          <Card key={dosing.id} className="hover:shadow-lg transition-all duration-300 bg-white border-burgundy/20">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-lg font-serif text-burgundy">{dosing.name}</CardTitle>
                  <Badge className="mt-2 bg-burgundy/10 text-burgundy">{dosing.category}</Badge>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setEditingDosingId(dosing.id);
                      setEditFormData({
                        name: dosing.name || '',
                        category: dosing.category || 'Peptide',
                        dosing_range: dosing.dosing_range || '',
                        frequency: dosing.frequency || '',
                        cycle_length: dosing.cycle_length || '',
                        timing: dosing.timing || '',
                        schedule: dosing.schedule || '',
                        fasted: dosing.fasted || 'No',
                        time_of_day: dosing.time_of_day || 'AM',
                        reconstitution: dosing.reconstitution || 'BAC Water',
                        titration_strategy: dosing.titration_strategy || '',
                        time_off_between_cycles: dosing.time_off_between_cycles || '',
                        uses_benefits: dosing.uses_benefits || '',
                        watchouts_precautions: dosing.watchouts_precautions || ''
                      });
                    }}
                    className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => deleteDosing(dosing.id)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {editingDosingId === dosing.id ? (
                // INLINE EDIT FORM
                <div className="max-h-[500px] overflow-y-auto">
                  <InlineDosingForm
                    formData={editFormData}
                    setFormData={setEditFormData}
                    onSave={async () => {
                      try {
                        await axios.put(`${API}/admin/dosing-database/${dosing.id}`, editFormData);
                        toast.success('Entry updated!');
                        setEditingDosingId(null);
                        fetchDosings();
                      } catch (error) {
                        console.error('Error updating entry:', error);
                        toast.error('Failed to update');
                      }
                    }}
                    onCancel={() => setEditingDosingId(null)}
                  />
                </div>
              ) : (
                // NORMAL DISPLAY
                <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-medium text-burgundy">Dosing Range:</span>
                  <p className="text-burgundy/70">{dosing.dosing_range}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy">Frequency:</span>
                  <p className="text-burgundy/70">{dosing.frequency}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy">Cycle Length:</span>
                  <p className="text-burgundy/70">{dosing.cycle_length}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy">Time Off:</span>
                  <p className="text-burgundy/70">{dosing.time_off_between_cycles || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy">Schedule:</span>
                  <p className="text-burgundy/70">{dosing.schedule}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy">Fasted:</span>
                  <p className="text-burgundy/70">{dosing.fasted}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy">Time of Day:</span>
                  <p className="text-burgundy/70">{dosing.time_of_day || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-burgundy">Reconstitution:</span>
                  <p className="text-burgundy/70">{dosing.reconstitution || 'N/A'}</p>
                </div>
              </div>
              
              <div className="pt-2 border-t border-burgundy/10">
                <span className="font-medium text-burgundy text-sm">Titration Strategy:</span>
                <p className="text-burgundy/70 text-sm mt-1 line-clamp-2">{dosing.titration_strategy || 'N/A'}</p>
              </div>
              
              {dosing.watchouts_precautions && (
                <div className="pt-2 border-t border-burgundy/10">
                  <span className="font-medium text-burgundy text-sm">Watchouts & Precautions:</span>
                  <p className="text-burgundy/70 text-sm mt-1 line-clamp-2">{dosing.watchouts_precautions}</p>
                </div>
              )}
              
              <div className="pt-2 border-t border-burgundy/10">
                <span className="font-medium text-burgundy text-sm">Uses & Benefits:</span>
                <p className="text-burgundy/70 text-sm mt-1 line-clamp-2">{dosing.uses_benefits}</p>
              </div>
                </>
              )}
            </CardContent>
          </Card>
        ))}

        {dosings.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Calculator className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-burgundy/60 mb-2 font-serif">No Dosing Entries</h3>
            <p className="text-burgundy/50 mb-4">Add your first dosing entry to help users with accurate calculations.</p>
            <Button 
              onClick={() => setShowAddForm(true)} 
              className="bg-burgundy hover:bg-burgundy/80 text-cream"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Entry
            </Button>
          </div>
        )}
      </div>

    </div>
  );
};

// Dosing Form Component
const DosingForm = ({ dosing = null, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: dosing?.name || '',
    category: dosing?.category || 'Peptide',
    dosing_range: dosing?.dosing_range || '',
    frequency: dosing?.frequency || '',
    cycle_length: dosing?.cycle_length || '',
    timing: dosing?.timing || '',
    schedule: dosing?.schedule || '',
    fasted: dosing?.fasted || 'No',
    time_of_day: dosing?.time_of_day || 'AM',
    fasted_state: dosing?.fasted_state || 'Fasted',
    reconstitution: dosing?.reconstitution || 'BAC Water',
    titration_strategy: dosing?.titration_strategy || '',
    time_off_between_cycles: dosing?.time_off_between_cycles || '',
    uses_benefits: dosing?.uses_benefits || '',
    watchouts_precautions: dosing?.watchouts_precautions || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (dosing) {
        await axios.put(`${API}/admin/dosing-database/${dosing.id}`, formData);
        toast.success('Dosing entry updated successfully!');
      } else {
        await axios.post(`${API}/admin/dosing-database`, formData);
        toast.success('Dosing entry created successfully!');
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving dosing entry:', error);
      toast.error('Error saving dosing entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
      style={{ 
        zIndex: 9999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}
    >
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-auto bg-white border-burgundy/20" style={{ position: 'relative', zIndex: 10000 }}>
        <CardHeader>
          <CardTitle className="font-serif text-burgundy flex justify-between items-center">
            {dosing ? 'Edit Dosing Entry' : 'Add New Dosing Entry'}
            <Button variant="ghost" onClick={onClose} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="e.g., BPC-157, Semaglutide"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Category</label>
                <select 
                  value={formData.category} 
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="border-burgundy/30 focus:ring-gold border rounded-md px-3 py-2 w-full"
                  required
                >
                  <option value="Peptide">Peptide</option>
                  <option value="Supplement">Supplement</option>
                  <option value="Hormone">Hormone</option>
                  <option value="Nootropic">Nootropic</option>
                </select>
              </div>
            </div>

            {/* Dosing Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Dosing Range</label>
                <Input
                  value={formData.dosing_range}
                  onChange={(e) => setFormData({...formData, dosing_range: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="e.g., 250mcg-1mg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Frequency</label>
                <Input
                  value={formData.frequency}
                  onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="e.g., 1-2x Daily"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Cycle Length</label>
                <Input
                  value={formData.cycle_length}
                  onChange={(e) => setFormData({...formData, cycle_length: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="e.g., 4-12 Weeks"
                  required
                />
              </div>
            </div>

            {/* Scheduling */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Timing</label>
                <Input
                  value={formData.timing}
                  onChange={(e) => setFormData({...formData, timing: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="e.g., AM/PM"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Schedule</label>
                <Input
                  value={formData.schedule}
                  onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="e.g., Daily, 5 On 2 Off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Fasted</label>
                <Select value={formData.fasted} onValueChange={(value) => setFormData({...formData, fasted: value})}>
                  <SelectTrigger className="border-burgundy/30 focus:ring-gold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Optional">Optional</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Time of Day */}
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Time of Day</label>
                <Select value={formData.time_of_day} onValueChange={(value) => setFormData({...formData, time_of_day: value})}>
                  <SelectTrigger className="border-burgundy/30 focus:ring-gold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AM">AM (Morning)</SelectItem>
                    <SelectItem value="PM">PM (Evening)</SelectItem>
                    <SelectItem value="Both">Both AM & PM</SelectItem>
                    <SelectItem value="Anytime">Anytime</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Fasted State */}
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Fasted State</label>
                <Select value={formData.fasted_state} onValueChange={(value) => setFormData({...formData, fasted_state: value})}>
                  <SelectTrigger className="border-burgundy/30 focus:ring-gold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fasted">Fasted</SelectItem>
                    <SelectItem value="Non-fasted">Non-fasted</SelectItem>
                    <SelectItem value="Either">Either</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reconstitution Method */}
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Reconstitution</label>
                <Select value={formData.reconstitution} onValueChange={(value) => setFormData({...formData, reconstitution: value})}>
                  <SelectTrigger className="border-burgundy/30 focus:ring-gold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BAC Water">BAC Water</SelectItem>
                    <SelectItem value="Acetic Acid + BAC">Acetic Acid + BAC</SelectItem>
                    <SelectItem value="Sodium Chloride">Sodium Chloride</SelectItem>
                    <SelectItem value="N/A">N/A (Pre-mixed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Advanced Information */}
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Titration Strategy</label>
              <Textarea
                value={formData.titration_strategy}
                onChange={(e) => setFormData({...formData, titration_strategy: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                rows={2}
                placeholder="Describe how to start low and gradually increase..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Time Off Between Cycles</label>
              <Input
                value={formData.time_off_between_cycles}
                onChange={(e) => setFormData({...formData, time_off_between_cycles: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                placeholder="e.g., 4-8 Weeks"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Uses & Benefits</label>
              <Textarea
                value={formData.uses_benefits}
                onChange={(e) => setFormData({...formData, uses_benefits: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                rows={2}
                placeholder="Describe the main uses and benefits..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Watchouts & Precautions</label>
              <Textarea
                value={formData.watchouts_precautions}
                onChange={(e) => setFormData({...formData, watchouts_precautions: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                rows={2}
                placeholder="Important safety information and precautions..."
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-burgundy hover:bg-burgundy/80 text-cream"
              >
                {loading ? 'Saving...' : (dosing ? 'Update Entry' : 'Create Entry')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Lazy Loading Image Component to prevent rate limiting

export { DosingDatabaseTab, DosingForm, InlineDosingForm };
export default DosingDatabaseTab;
